import React, { useState, useEffect } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Typography, 
    Table, 
    Tag, 
    Button, 
    Space, 
    DatePicker, 
    Select, 
    message, 
    Spin 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    FilePdfOutlined, 
    ArrowLeftOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PDFDownloadLink } from '@react-pdf/renderer';
import MemberDetailedPDF from './MemberDetailedPDF';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const MemberDetailedReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        status: '',
        gender: '',
        birthday: [null, null],
        activeTill: [null, null]
    });

    const columns = [
        {
            title: 'Member ID',
            dataIndex: 'member_id',
            key: 'member_id',
            fixed: 'left',
            width: 120,
        },
        {
            title: 'Full Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 180,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={Number(status) === 1 ? 'green' : 'red'}>
                    {Number(status) === 1 ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Active Till',
            dataIndex: 'active_till',
            key: 'active_till',
            width: 120,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        },
        {
            title: 'NIC',
            dataIndex: 'nic',
            key: 'nic',
            width: 130,
        },
        {
            title: 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            render: (g) => g === 'M' ? 'Male' : g === 'F' ? 'Female' : 'Other'
        },
        {
            title: 'Birthday',
            dataIndex: 'birthday',
            key: 'birthday',
            width: 120,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        },
        {
            title: 'Contact',
            dataIndex: 'contact_no',
            key: 'contact_no',
            width: 130,
        },
        {
            title: 'Emergency Contact',
            dataIndex: 'emergency_contact',
            key: 'emergency_contact',
            width: 150,
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            width: 250,
        },
        {
            title: 'T-Shirt',
            dataIndex: 'tshirt_size',
            key: 'tshirt_size',
            width: 90,
        },
        {
            title: 'Trouser',
            dataIndex: 'trouser_size',
            key: 'trouser_size',
            width: 90,
        },
        {
            title: 'Shoe',
            dataIndex: 'shoe_size',
            key: 'shoe_size',
            width: 80,
        },
        {
            title: 'Assigned Trainers',
            dataIndex: 'assigned_trainers',
            key: 'assigned_trainers',
            width: 250,
            render: (text) => text || 'None'
        },
        {
            title: 'Assigned Members',
            dataIndex: 'assigned_members',
            key: 'assigned_members',
            width: 250,
            render: (text) => text || 'None'
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.gender) params.append('gender', filters.gender);
            
            if (filters.birthday[0] && filters.birthday[1]) {
                params.append('birthday_start', filters.birthday[0].format('YYYY-MM-DD'));
                params.append('birthday_end', filters.birthday[1].format('YYYY-MM-DD'));
            }
            
            if (filters.activeTill[0] && filters.activeTill[1]) {
                params.append('active_till_start', filters.activeTill[0].format('YYYY-MM-DD'));
                params.append('active_till_end', filters.activeTill[1].format('YYYY-MM-DD'));
            }

            const response = await axiosClient.get(`/reports/get_detailed_member_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No records found for the selected filters.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch report data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            status: '',
            gender: '',
            birthday: [null, null],
            activeTill: [null, null]
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Member ID': item.member_id,
            'Status': Number(item.status) === 1 ? 'Active' : 'Inactive',
            'Active Till': item.active_till,
            'NIC': item.nic,
            'Full Name': item.name,
            'Gender': item.gender === 'M' ? 'Male' : item.gender === 'F' ? 'Female' : 'Other',
            'Birthday': item.birthday,
            'Contact Number': item.contact_no,
            'Emergency Contact': item.emergency_contact,
            'Address': item.address,
            'T-Shirt Size': item.tshirt_size,
            'Trouser Size': item.trouser_size,
            'Shoe Size': item.shoe_size,
            'Assigned Trainers': item.assigned_trainers,
            'Assigned Members': item.assigned_members
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Members");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Member_Detailed_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <TeamOutlined />
                            <span>Member List Excel Export</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Status"
                                value={filters.status}
                                onChange={(val) => setFilters({...filters, status: val})}
                            >
                                <Select.Option value="">All</Select.Option>
                                <Select.Option value="1">Active</Select.Option>
                                <Select.Option value="0">Inactive</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Gender</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Gender"
                                value={filters.gender}
                                onChange={(val) => setFilters({...filters, gender: val})}
                            >
                                <Select.Option value="">All</Select.Option>
                                <Select.Option value="M">Male</Select.Option>
                                <Select.Option value="F">Female</Select.Option>
                                <Select.Option value="O">Other</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Birthday Range</div>
                            <RangePicker 
                                style={{ width: '100%' }} 
                                value={filters.birthday}
                                onChange={(dates) => setFilters({...filters, birthday: dates})}
                            />
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Active Till Range</div>
                            <RangePicker 
                                style={{ width: '100%' }} 
                                value={filters.activeTill}
                                onChange={(dates) => setFilters({...filters, activeTill: dates})}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} records)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Space wrap>
                                <Button 
                                    icon={<FileExcelOutlined />} 
                                    onClick={exportToExcel}
                                    style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                                >
                                    Excel
                                </Button>
                                <PDFDownloadLink
                                    document={<MemberDetailedPDF data={data} filters={filters} />}
                                    fileName={`Member_Detailed_Report_${dayjs().format('YYYYMMDD')}.pdf`}
                                >
                                    {({ loading: pdfLoading }) => (
                                        <Button 
                                            danger 
                                            icon={<FilePdfOutlined />} 
                                            loading={pdfLoading}
                                        >
                                            PDF
                                        </Button>
                                    )}
                                </PDFDownloadLink>
                            </Space>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="id"
                        scroll={{ x: 2400 }}
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100']
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default MemberDetailedReport;
